import type { Dispatch, SetStateAction } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { attachmentsApi, type FormAttachment } from '@/api/attachmentsApi'

const attachmentsKey = (submissionId: string) => ['attachments', submissionId] as const

/**
 * Key for the procedure-wide batch read.
 *
 * Deliberately a sibling of the single-submission keys under the same
 * `['attachments']` prefix, so an upload can reach both with one invalidation.
 * The literal `'batch'` segment is what keeps the two apart; submission ids are
 * UUIDs, so it cannot be mistaken for one.
 */
const attachmentsBatchKey = (submissionIds: string[]) =>
  ['attachments', 'batch', submissionIds.join(',')] as const

/**
 * Every attachment on one submission, plus upload/remove mutations.
 *
 * Fetched per submission rather than per field: a form renders many attachable
 * fields at once, and one list request is cheaper than N. Consumers index the
 * result by `fieldId`.
 */
export function useAttachments(submissionId: string | undefined) {
  const qc = useQueryClient()

  const invalidate = () => {
    if (!submissionId) return
    void qc.invalidateQueries({ queryKey: attachmentsKey(submissionId) })
    // The procedure-wide strip reads its own batch key, so invalidating only
    // this submission's query would leave it showing a file that had just been
    // removed — or missing one just added.
    void qc.invalidateQueries({ queryKey: ['attachments', 'batch'] })
  }

  const list = useQuery({
    queryKey: attachmentsKey(submissionId ?? ''),
    queryFn: () => attachmentsApi.list(submissionId!),
    enabled: !!submissionId,
  })

  const upload = useMutation({
    mutationFn: ({ fieldId, file }: { fieldId: string; file: File }) =>
      attachmentsApi.upload(submissionId!, fieldId, file),
    onSuccess: invalidate,
  })

  /** `attachmentId` names one file on a multi-file field; omit it otherwise. */
  const remove = useMutation({
    mutationFn: (arg: string | { fieldId: string; attachmentId?: string }) => {
      const { fieldId, attachmentId } = typeof arg === 'string' ? { fieldId: arg, attachmentId: undefined } : arg
      return attachmentsApi.remove(submissionId!, fieldId, attachmentId)
    },
    onSuccess: invalidate,
  })

  /** fieldId → attachment, for O(1) lookup while rendering fields. */
  const byFieldId = new Map<string, FormAttachment>(
    (list.data ?? []).map(a => [a.fieldId, a]),
  )

  /** fieldId → every file on it, in slot order — what a multi-file field lists. */
  const filesByFieldId = new Map<string, FormAttachment[]>()
  for (const a of list.data ?? []) {
    const files = filesByFieldId.get(a.fieldId)
    if (files) files.push(a)
    else filesByFieldId.set(a.fieldId, [a])
  }
  for (const files of filesByFieldId.values()) files.sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0))

  return { list, byFieldId, filesByFieldId, upload, remove }
}

/**
 * Fetch a fresh download URL and open it. Not a query — the URL is short-lived
 * and single-use, so caching one would just hand back an expired link.
 */
export async function openAttachment(
  submissionId: string,
  fieldId: string,
  attachmentId?: string,
): Promise<void> {
  const { url } = await attachmentsApi.getDownloadUrl(submissionId, fieldId, attachmentId)
  window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * Attachments across several submissions at once — the evidence already filed on
 * a procedure's EARLIER steps, so someone filling a later form can see it without
 * navigating away and losing their place.
 *
 * **One batch request, not one per submission (z8nrz7cnhz).** This used to be
 * `useQueries` with a query per id, which cached and invalidated each entry
 * individually — tidy, but it meant opening a case fired a burst of N requests
 * at once. Against the API's 120/min per-caller ceiling that was a large part of
 * how a clinician came to see `ThrottlerException` where the patient's case
 * should have been, and the burst grew with the length of the journey — so the
 * longest-running cases, the ones most worth looking at, failed most reliably.
 *
 * The per-submission invalidation that shape bought is preserved by
 * `useAttachments` invalidating the batch prefix alongside its own key, so an
 * upload on the open step still refreshes this strip.
 */
export function useAttachmentsForSubmissions(submissionIds: string[]) {
  // Sorted so the same set of steps arriving in a different order is one cache
  // entry rather than two — the journey's step order is not stable across
  // refetches, and the server's answer does not depend on it.
  const ids = [...submissionIds].sort()

  const query = useQuery({
    queryKey: attachmentsBatchKey(ids),
    queryFn: () => attachmentsApi.listForSubmissions(ids),
    enabled: ids.length > 0,
  })

  // Only non-empty entries are surfaced, matching what callers already expect:
  // they treat a missing key as "nothing to show".
  const bySubmissionId = new Map<string, FormAttachment[]>()
  for (const [id, found] of Object.entries(query.data ?? {})) {
    if (found.length > 0) bySubmissionId.set(id, found)
  }
  return bySubmissionId
}

/** Per-slot upload state, keyed by field id or table cell key. */
export type AttachmentSlotState = Record<string, { busy: boolean; error?: string }>

/**
 * The upload/remove handlers for one slot, wired to a shared busy/error map.
 *
 * Four things now upload against this pipeline — the attachment icon, the Image
 * field, and image cells in both table renderers — and each needs the identical
 * fifteen lines: mark busy, mutate, clear on success, keep the message on
 * failure. Written out per call site they drift, and the one that drifts
 * silently is the error branch: a slot that fails and never clears `busy` stays
 * disabled with no way back except a reload.
 *
 * `key` is the field id for a field, or the `rowId__colId` cell key for a table
 * cell — the attachment API takes either, since a cell is addressed as a field.
 */
export function attachmentSlotHandlers(
  key: string,
  attachments: Pick<ReturnType<typeof useAttachments>, 'upload' | 'remove'>,
  setState: Dispatch<SetStateAction<AttachmentSlotState>>,
) {
  const mark = (slot: { busy: boolean; error?: string }) =>
    setState(prev => ({ ...prev, [key]: slot }))
  const settle = {
    onError: (err: Error) => mark({ busy: false, error: err.message }),
    onSuccess: () => mark({ busy: false }),
  }
  return {
    onUpload: (file: File) => {
      mark({ busy: true })
      attachments.upload.mutate({ fieldId: key, file }, settle)
    },
    onRemove: () => {
      mark({ busy: true })
      attachments.remove.mutate(key, settle)
    },
  }
}

/**
 * Handlers for a multi-file Attachment field (z8nrz7dp7p), on the same busy/error
 * map as `attachmentSlotHandlers`.
 *
 * Files go up ONE AT A TIME. The server gives each confirmed file the lowest free
 * slot, and two confirms racing for the same slot would have one refused with a
 * conflict — so a batch is uploaded in order, and one file failing does not stop
 * the rest. Every failure is kept, named by file, in the field's error.
 */
export function attachmentListHandlers(
  fieldId: string,
  attachments: Pick<ReturnType<typeof useAttachments>, 'upload' | 'remove'>,
  setState: Dispatch<SetStateAction<AttachmentSlotState>>,
) {
  const mark = (slot: { busy: boolean; error?: string }) =>
    setState(prev => ({ ...prev, [fieldId]: slot }))
  return {
    onUploadFiles: async (files: File[]) => {
      if (files.length === 0) return
      mark({ busy: true })
      const failures: string[] = []
      for (const file of files) {
        try {
          await attachments.upload.mutateAsync({ fieldId, file })
        } catch (err) {
          failures.push(`${file.name}: ${(err as Error).message}`)
        }
      }
      mark({ busy: false, error: failures.length > 0 ? failures.join(' ') : undefined })
    },
    onRemove: (attachmentId: string) => {
      mark({ busy: true })
      attachments.remove.mutate(
        { fieldId, attachmentId },
        {
          onError: (err: Error) => mark({ busy: false, error: err.message }),
          onSuccess: () => mark({ busy: false }),
        },
      )
    },
  }
}
